<template>
  <div :style="containerStyle">
    <div :style="headerContainerStyle">
      <div :style="searchBoxContainerStyle">
        <SearchBoxPrimary placeholder="Search"
                          v-model="input"
                          @search="onSearch" />
      </div>
      <div :style="textStyle">
        Total count
        <div :style="countTextStyle">
          {{ dataCount }}
        </div>
      </div>
    </div>
    <CardPrimary :style="cardStyle">
      <div :style="tableContainerStyle">
        <template v-if="isSearching">
          <LoaderPrimary :loading="true" />
        </template>
        <template v-else-if="hasTenantUsers">
          <div :style="scrollableColumnsContainerStyle">
            <div :style="emptyColumnsContainerStyle">
              <TableEmptyColumnPrimary :rowsLength="tenantUsers.length" />
            </div>
            <div :style="userColumnContainerStyle">
              <TableUserColumnPrimary header="Name"
                                      :rows="tenantUsers"
                                      :truncate="true" />
            </div>
            <div :style="roleColumnContainerStyle">
              <TableColumnPrimary header="Role"
                                  :rows="roles"
                                  :truncate="true" />
            </div>
            <div :style="lastLoginDateColumnContainerStyle">
              <TableColumnPrimary header="Last login"
                                  :rows="lastLoginDates"
                                  :truncate="true" />
            </div>
            <div :style="joinedDateColumnContainerStyle">
              <TableColumnPrimary header="Joined"
                                  :rows="joinedDates"
                                  :truncate="true" />
            </div>
          </div>
          <div :style="fixedColumnsContainerStyle">
            <TablePopoverColumnPrimary iconContent="more_horiz"
                                       :options="options"
                                       :rowsLength="tenantUsers.length"
                                       @select-action="(optionIndex, rowIndex) => onSelectAction(optionIndex, rowIndex)"/>
            <div :style="emptyColumnsContainerStyle">
              <TableEmptyColumnPrimary :rowsLength="tenantUsers.length" />
            </div>
          </div>
        </template>
        <template v-else>
          <NoDataToShow>
            There is no data to show.
          </NoDataToShow>
        </template>
      </div>
    </CardPrimary>
    <div :style="paginationContainerStyle">
      <PaginationPrimary display="block"
                         v-model="currentPage"
                         :pageNum="tenantUsersPageNum" />
    </div>
  </div>
</template>

<script>
  import CardPrimary from '@modules/card/card-primary'
  import LoaderPrimary from '@modules/loader/loader-primary'
  import NoDataToShow from '@modules/error/no-data-to-show'
  import PaginationPrimary from '@modules/pagination/pagination-primary'
  import SearchBoxPrimary from '@modules/search-box/search-box-primary'
  import TableColumnPrimary from '@modules/table/table-column-primary'
  import TableEmptyColumnPrimary from '@modules/table/table-empty-column-primary'
  import TablePopoverColumnPrimary from '@modules/table/table-popover-column-primary'
  import TableUserColumnPrimary from '@modules/table/table-user-column-primary'
  import client from '@js/client'
  import resources from '@js/resources'
  import { FontStyle, Layout } from '@js/styles'
  import { ROLE_TYPE_DISPLAY_NAMES, ROLE_TYPE_CODES } from '@js/constants'
  import { camelKeyToSnakeKey } from '@js/str-utils'
  import { getLocaleDateString } from '@js/date-utils'
  import { mapGetters, mapState } from 'vuex'
  import { MODAL_WINDOW_NAMES } from '@js/constants'

  export default {
    name: 'AdminUsersUsers',
    components: {
      CardPrimary,
      LoaderPrimary,
      NoDataToShow,
      PaginationPrimary,
      SearchBoxPrimary,
      TableColumnPrimary,
      TableEmptyColumnPrimary,
      TablePopoverColumnPrimary,
      TableUserColumnPrimary,
    },
    data: function () {
      return {
        cardStyle: {
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          minHeight: '60px',
          width: '100%',
        },
        currentPage: 1,
        containerStyle: {
        },
        countTextStyle: {
          display: 'inline-block',
          lineHeight: FontStyle.size.md,
          fontSize: FontStyle.size.md,
          fontWeight: FontStyle.weight.xl,
        },
        emptyColumnsContainerStyle: {
          width: Layout.space.md,
        },
        fixedColumnsContainerStyle: {
          display: 'flex',
          width: '48px',
        },
        headerContainerStyle: {
          alignItems: 'flex-end',
          display: 'flex',
          height: '36px',
          justifyContent: 'space-between',
          margin: '32px 0 16px 0',
          position: 'relative',
          width: '100%',
        },
        input: '',
        isSearching: false,
        joinedDateColumnContainerStyle: {
          minWidth: '100px',
          width: '20%',
        },
        lastLoginDateColumnContainerStyle: {
          minWidth: '100px',
          width: '20%',
        },
        options: [
          { label: 'Promote to Admin', action: 'promoteRoleType' },
          { label: 'Revoke Admin role', action: 'demoteRoleType' },
          { label: 'Delete from workspace', action: 'deleteTenantUser' },
        ],
        paginationContainerStyle: {
          alignItems: 'center',
          display: 'flex',
          height: '32px',
          justifyContent: 'center',
          margin: `${Layout.space.lg} 0`,
          width: '100%',
        },
        roleColumnContainerStyle: {
          minWidth: '70px',
          width: '15%',
        },
        scrollableColumnsContainerStyle: {
          display: 'flex',
          flex: 1,
          height: '100%',
          justifyContent: 'space-between',
          overflowX: 'scroll',
        },
        searchBoxContainerStyle: {
          width: '240px',
        },
        tableContainerStyle: {
          alignItems: 'flex-start',
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        },
        textStyle: {
          fontSize: FontStyle.size.md,
          fontWeight: FontStyle.weight.md,
        },
        userColumnContainerStyle: {
          flex: 1,
        },
      };
    },
    created: function () {
      this.countTextStyle = {
        ...this.countTextStyle,
        color: this.color.dark2(),
      };

      this.textStyle = {
        ...this.textStyle,
        color: this.color.dark2(),
      };
    },
    mounted: async function () {
      await this.fetchTenantUsers(this.currentPage);
    },
    computed: {
      ...mapGetters('adminData', ['hasTenantUsers', 'tenantUsersPageNum']),
      ...mapGetters('colorTheme', ['color']),
      ...mapState({
        dataCount: state => state.adminData.tenantUsers.count,
        tenantDomain: state => state.tenant.domain,
        tenantUserId: state => state.tenantUser.id,
        tenantUsers: state => state.adminData.tenantUsers.data,
        timezone: state => state.user.timezoneCode,
      }),
      users: function () {
        return this.tenantUsers.map(elem => elem.user);
      },
      roles: function () {
        return this.tenantUsers.map(elem => ROLE_TYPE_DISPLAY_NAMES[elem.roleType]);
      },
      lastLoginDates: function () {
        return this.users.map(elem => getLocaleDateString(elem.lastLogin, this.timezone));
      },
      joinedDates: function () {
        return this.tenantUsers.map(elem => getLocaleDateString(elem.joinedAt, this.timezone));
      },
    },
    methods: {
      deleteTenantUser: async function (tenantUser, userName) {
        this.$modal.show(
            MODAL_WINDOW_NAMES.DELETE_TENANT_USER_PRIMARY, { tenantUser: tenantUser });
      },
      demoteTenantUser: async function (tenantUser, userName) {
        try {
          const url = resources['roleTypes'](
            this.tenantDomain, this.tenantUserId, tenantUser.id);
          const payload = camelKeyToSnakeKey({
            roleType: ROLE_TYPE_CODES.GENERAL,
          });
          const res = await client.put(url, payload);
          await this.$store.commit(
            'notification/setMessage', `Assigned "${userName}" to Member`);
        } catch (error) {
          this.$store.commit(
            'notification/setWarning', 'Failed to demote user');
          throw error;
        }
      },
      fetchTenantUsers: async function (page) {
        try {
          this.isSearching = true;
          await this.$store.dispatch('adminData/fetchTenantUsers', { search: this.input, page: page });
        } catch (error) {
          console.error(error);
        }
        this.isSearching = await false;
      },
      onSearch: async function () {
        if (this.currentPage === 1) {
          await this.fetchTenantUsers(1);
        } else {
          this.currentPage = 1;
        }
      },
      onSelectAction: async function (optionIndex, rowIndex) {
        const action = this.options[optionIndex].action;
        const tenantUser = this.tenantUsers[rowIndex];
        const userName = `${tenantUser.user.firstName} ${tenantUser.user.lastName}`;

        try {
          if (action === 'promoteRoleType') {
            await this.promoteTenantUser(tenantUser, userName);
          } else if (action === 'demoteRoleType') {
            await this.demoteTenantUser(tenantUser, userName);
          } else if (action === 'deleteTenantUser') {
            await this.deleteTenantUser(tenantUser, userName);
          } else {
            throw Error('Invalid action is selected.');
          }
          await this.fetchTenantUsers(this.currentPage);
        } catch (error) {
          console.error(error);
        }
      },
      promoteTenantUser: async function (tenantUser, userName) {
        try {
          const url = resources['roleTypes'](
            this.tenantDomain, this.tenantUserId, tenantUser.id);
          const payload = camelKeyToSnakeKey({
            roleType: ROLE_TYPE_CODES.ADMIN,
          });
          const res = await client.put(url, payload);
          await this.$store.commit(
            'notification/setMessage', `Assigned "${userName}" to Admin`);
        } catch (error) {
          this.$store.commit(
            'notification/setWarning', 'Failed to promote user');
          throw error;
        }
      },
    },
    watch: {
      currentPage: async function (newVal, oldVal) {
        await this.fetchTenantUsers(newVal);
        await window.scrollTo(0, 0);
      },
    },
  };
</script>
